import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import type { BatchContents, BatchFolder, BatchJob, BatchPanel, CropTrace } from "../../api/client";
import { FolderIcon, PdfIcon } from "./FileIcons";
import { CropReviewModal } from "./CropReviewModal";

interface Upload {
  name: string;
  status: "queued" | "processing" | "done" | "failed";
  panels?: number;
  error?: string;
}

interface LogEntry {
  id: number;
  time: string;
  kind: "start" | "done" | "fail" | "info";
  text: string;
}

/** A picked PDF plus the sub-path it sat in, so folder structure is preserved. */
interface Picked {
  file: File;
  dir: string; // "" when the file was picked on its own
}

/** How often to ask the server how the worker queue is getting on. */
const POLL_MS = 3000;

const clock = () => new Date().toLocaleTimeString([], { hour12: false });

/** Mirror the server's folder-name rule so a dropped tree can't 400 mid-run. */
function sanitizeSegment(s: string): string {
  return (s.replace(/[^A-Za-z0-9 _.\-()&,]/g, "_").trim() || "untitled").slice(0, 64);
}

const sanitizeDir = (dir: string) =>
  dir.split("/").filter(Boolean).map(sanitizeSegment).join("/");

const isPdf = (name: string) => name.toLowerCase().endsWith(".pdf");

/** Stable label for a picked file — two folders may hold the same filename. */
const key = (p: Picked) => (p.dir ? `${p.dir}/${p.file.name}` : p.file.name);

/** Walk a dropped directory tree via the webkit entries API. */
async function walkEntry(entry: any, dir: string, out: Picked[]): Promise<void> {
  if (entry.isFile) {
    const file: File = await new Promise((res, rej) => entry.file(res, rej));
    if (isPdf(file.name)) out.push({ file, dir });
    return;
  }
  if (!entry.isDirectory) return;

  const childDir = dir ? `${dir}/${entry.name}` : entry.name;
  const reader = entry.createReader();
  // readEntries yields at most ~100 per call, so keep reading until it's empty.
  for (;;) {
    const batch: any[] = await new Promise((res, rej) => reader.readEntries(res, rej));
    if (batch.length === 0) break;
    for (const e of batch) await walkEntry(e, childDir, out);
  }
}

function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return s % 60 === 0 ? `${m}m` : `${m}m ${s % 60}s`;
}

/** Root listing reuses the contents shape so the render path stays uniform. */
const EMPTY: BatchContents = { path: "", folders: [], jobs: [], images: [] };


/**
 * Point finding for one crop, and the hand-off to WPD.
 *
 * Three states the operator needs to tell apart, so each looks different: a
 * crop nobody has traced, one being traced, and one whose curves are found.
 * Only the last can be opened in the editor.
 *
 * `tuned: false` is surfaced rather than hidden. It means the crop was traced
 * with the generic constants because its folder was never categorised, so the
 * curves may look right while being measured against the wrong rules — the
 * operator should see that before approving anything.
 */
/**
 * Inline rename for a job's source-PDF filename.  Clicking the pencil turns
 * the label into a text input; Enter commits, Esc cancels.  The button lives
 * inside a parent `<button>` (the accordion toggle) so we stop propagation on
 * click to avoid collapsing / expanding the row when the operator starts to
 * edit.
 */
function JobNameEdit({
  jobName,
  onRename,
}: {
  jobName: string;
  onRename: (next: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(jobName);
  const commit = async () => {
    const next = draft.trim();
    if (!next || next === jobName) { setEditing(false); return; }
    await onRename(next);
    setEditing(false);
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); void commit(); }
            if (e.key === "Escape") { setEditing(false); setDraft(jobName); }
          }}
          className="flex-1 bg-rdp-bg border border-orange-500 rounded px-1.5 py-0.5 text-slate-200 text-xs"
        />
        <button
          onClick={(e) => { e.stopPropagation(); void commit(); }}
          className="text-[10px] text-emerald-400 hover:text-emerald-300 border border-emerald-600/40 rounded px-1.5 py-0.5"
        >
          ✓
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setEditing(false); setDraft(jobName); }}
          className="text-[10px] text-slate-500 hover:text-slate-300 border border-slate-700 rounded px-1.5 py-0.5"
        >
          ✕
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <p className="text-slate-300 truncate font-medium">{jobName}</p>
      <button
        onClick={(e) => { e.stopPropagation(); setDraft(jobName); setEditing(true); }}
        title="Rename this source PDF"
        className="text-[11px] text-slate-500 hover:text-slate-200 border border-slate-700 rounded px-1"
      >
        ✏
      </button>
    </div>
  );
}


/**
 * Compact list of saved WPD exports for one panel, rendered directly under
 * CropActions on the folder tile so an operator can grab any variant
 * without opening the review modal.  Silent when no jobId is present
 * (loose images have no exports) or when the panel has none saved.
 */
// Kept in sync with CHART_CATEGORIES in CropReviewModal.tsx.  Duplicated
// (rather than imported) to keep the tile self-contained; the list is short
// and rarely churns.
const TILE_CATEGORIES: { id: string; label: string }[] = [
  { id: "",              label: "— auto —" },
  { id: "dual_carpet",   label: "Dual Carpet ⚙" },
  { id: "mesh_chart",    label: "Mesh / Carpet" },
  { id: "wind",          label: "Wind" },
  { id: "airport_oat",   label: "Airport OAT" },
  { id: "gross_weight",  label: "Gross Weight" },
  { id: "climb_limits",  label: "Climb Limits" },
  { id: "left_axis",     label: "Left-axis OAT" },
  { id: "bottom_axis",   label: "Bottom-axis fan" },
  { id: "vertical_fan",  label: "Vertical fan" },
  { id: "hatch_band",    label: "Hatch band" },
  { id: "defaults_checked", label: "Defaults" },
  { id: "generic",       label: "Generic" },
];

function PanelExports({ path, jobId, filename }: {
  path: string;
  jobId?: string;
  filename: string;
}) {
  const [items, setItems] = useState<{
    name: string; size: number; saved_at: string | null;
    category: string | null;
    effective_category: string | null;
    category_source: "manual" | "folder" | "metadata" | "none";
  }[] | null>(null);
  const [traces, setTraces] = useState<Record<string, {
    status: string; curve_count?: number; confidence?: number; error?: string;
  }>>({});
  const [pending, setPending] = useState<Set<string>>(new Set());

  const refresh = () => {
    if (!jobId) { setItems([]); return; }
    api.batch.listExports(path, jobId, filename)
      .then((r) => {
        setItems(r.exports);
        // Fetch trace status for each in parallel.
        void Promise.all(r.exports.map(async (ex) => {
          try {
            const t = await api.batch.getExportTrace(path, jobId, filename, ex.name);
            return [ex.name, t] as const;
          } catch {
            return [ex.name, { status: "none" }] as const;
          }
        })).then((pairs) => {
          setTraces(Object.fromEntries(pairs));
        });
      })
      .catch(() => setItems([]));
  };
  useEffect(() => { refresh(); }, [path, jobId, filename]);

  // Poll running traces every 3s so the tile animates without a manual reload.
  useEffect(() => {
    if (!jobId) return;
    const runningNames = Object.entries(traces)
      .filter(([, t]) => t.status === "running")
      .map(([n]) => n);
    if (runningNames.length === 0) return;
    const id = setInterval(async () => {
      const updates: typeof traces = {};
      for (const n of runningNames) {
        try {
          const t = await api.batch.getExportTrace(path, jobId, filename, n);
          updates[n] = t;
        } catch { /* skip */ }
      }
      setTraces((prev) => ({ ...prev, ...updates }));
    }, 3000);
    return () => clearInterval(id);
  }, [path, jobId, filename, traces]);

  const traceIt = async (name: string) => {
    if (!jobId) return;
    setPending((s) => new Set(s).add(name));
    try {
      await api.batch.traceExport(path, jobId, filename, name);
      setTraces((t) => ({ ...t, [name]: { status: "running" } }));
    } catch (e) {
      setTraces((t) => ({ ...t, [name]: {
        status: "failed",
        error: e instanceof Error ? e.message : String(e),
      }}));
    } finally {
      setPending((s) => { const n = new Set(s); n.delete(name); return n; });
    }
  };

  // Assemble the projectid WPD asks the server for, and open the wrapper.
  // The server route in wpd_api.serve_wpd_project_tar recognises the
  // "export-<b64>" prefix and streams the S3-stored export tar back to WPD,
  // which then renders the calibration + traced points inline.
  const openInWpd = (name: string) => {
    if (!jobId) return;
    // Include a per-click timestamp so the projectid — and therefore the
    // tar URL WPD requests — is unique every time.  Without it, the browser
    // (and WPD's own project cache) happily reuses the previous tar when
    // the operator re-creates an export with the same name.  Backend
    // ignores unknown keys in the payload.
    const payload = { path, job_id: jobId, panel: filename, name, _ts: Date.now() };
    const b64 = btoa(JSON.stringify(payload))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    const projectid = `export-${b64}`;
    window.open(`/hitl-editor/wpd/index.html?projectid=${projectid}`, "_blank");
  };

  if (!jobId || !items || items.length === 0) return null;
  return (
    <div className="border-t border-rdp-border/60 mt-1 pt-1.5 px-2 pb-2 space-y-1">
      <p className="text-[9px] text-slate-500 uppercase tracking-wide">
        WPD exports ({items.length})
      </p>
      <ul className="space-y-1">
        {items.map((ex) => {
          const t = traces[ex.name];
          return (
            <li key={ex.name} className="flex flex-wrap items-center gap-1 text-[10px]">
              <a
                href={api.batch.exportUrl(path, jobId, filename, ex.name)}
                download
                className="flex-1 min-w-0 text-emerald-400 hover:text-emerald-300 underline decoration-dotted underline-offset-2 truncate"
                title={ex.saved_at ? new Date(ex.saved_at).toLocaleString() : undefined}
              >
                ⤓ {ex.name}
              </a>
              {t?.status === "done" && (
                <span
                  className="text-emerald-500 tabular-nums"
                  title={`${t.curve_count ?? 0} curves · ${((t.confidence ?? 0) * 100).toFixed(0)}% confidence`}
                >
                  ✓{t.curve_count ?? 0}
                </span>
              )}
              {t?.status === "running" && (
                <span className="text-blue-400 animate-pulse">●</span>
              )}
              {t?.status === "failed" && (
                <span className="text-red-400" title={t.error}>✗</span>
              )}
              <select
                value={ex.category ?? ""}
                onChange={async (e) => {
                  if (!jobId) return;
                  const c = e.target.value;
                  try {
                    await api.batch.patchExportCategory(path, jobId, filename, ex.name, c);
                    setItems((prev) => prev?.map((x) => x.name === ex.name
                      ? { ...x, category: c || null,
                          effective_category: c || x.effective_category,
                          category_source: c ? "manual" as const : x.category_source }
                      : x) ?? null);
                  } catch { /* ignore */ }
                }}
                className={`text-[10px] bg-rdp-bg border rounded px-1 py-0 max-w-[80px] truncate ${
                  ex.category
                    ? "border-emerald-600/60 text-emerald-300"
                    : ex.effective_category
                      ? "border-amber-600/50 text-amber-300"
                      : "border-slate-700 text-slate-400"
                }`}
                title={
                  ex.category
                    ? `Manual override: ${ex.category}`
                    : ex.effective_category
                      ? `Auto (${ex.category_source}): ${ex.effective_category}`
                      : "No category — tracer falls back to generic (tuned=false)."
                }
              >
                {TILE_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
              <button
                onClick={() => void traceIt(ex.name)}
                disabled={pending.has(ex.name) || t?.status === "running"}
                title="Run the tracer using this export's calibration + chart type; the tar is updated in place"
                className="text-[10px] text-orange-400 hover:text-orange-300 border border-orange-600/40 rounded px-1 disabled:opacity-50"
              >
                🎯
              </button>
              <button
                onClick={() => openInWpd(ex.name)}
                title="Open this export tar directly in WPD (our server-hosted WebPlotDigitizer)"
                className="text-[10px] text-blue-400 hover:text-blue-300 border border-blue-600/40 rounded px-1"
              >
                → WPD
              </button>
              <button
                onClick={async () => {
                  if (!jobId) return;
                  if (!window.confirm(`Delete export "${ex.name}"?`)) return;
                  setPending((s) => new Set(s).add(ex.name));
                  try {
                    await api.batch.deleteExport(path, jobId, filename, ex.name);
                    setItems((prev) => prev?.filter((x) => x.name !== ex.name) ?? null);
                    setTraces((prev) => {
                      const { [ex.name]: _, ...rest } = prev;
                      return rest;
                    });
                  } catch (e) {
                    window.alert(`Delete failed: ${e instanceof Error ? e.message : String(e)}`);
                  } finally {
                    setPending((s) => { const n = new Set(s); n.delete(ex.name); return n; });
                  }
                }}
                disabled={pending.has(ex.name)}
                title="Delete this export (tar + snapshot + trace status)"
                className="text-[10px] text-red-400 hover:text-red-300 border border-red-600/40 rounded px-1 disabled:opacity-50"
              >
                🗑
              </button>
              <span className="text-slate-600 tabular-nums">
                {(ex.size / 1024).toFixed(0)}K
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}


/**
 * Small modal that shows the pretty-printed OCR JSON for one panel — both
 * the raw LLM response and the merged state that the tracer + WPD export
 * will use.  Used from the CropActions "🔎 JSON" link so the operator can
 * audit what the AI said and what actually got applied.
 */
function OcrJsonViewer({ path, jobId, filename, onClose }: {
  path: string;
  jobId?: string;
  filename: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!jobId) { setErr("Loose images have no OCR JSON."); return; }
    api.batch.getOcr(path, jobId, filename)
      .then((r) => setData(r as unknown as Record<string, unknown>))
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [path, jobId, filename]);
  const pretty = data ? JSON.stringify(data, null, 2) : "";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-rdp-card border border-rdp-border rounded-xl shadow-2xl w-[92vw] max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-rdp-border">
          <div>
            <p className="text-sm font-semibold text-slate-200">
              OCR JSON — {filename}
            </p>
            <p className="text-[10px] text-slate-500">
              Includes raw AI response (<span className="font-mono">ai_response</span>) and merged state.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {data && (
              <>
                <button
                  onClick={() => { void navigator.clipboard.writeText(pretty); }}
                  className="text-[11px] text-slate-400 hover:text-slate-200 border border-slate-600 rounded px-2 py-0.5"
                  title="Copy to clipboard"
                >
                  📋 Copy
                </button>
                <a
                  href={URL.createObjectURL(new Blob([pretty], { type: "application/json" }))}
                  download={`${filename}.ocr.json`}
                  className="text-[11px] text-emerald-400 hover:text-emerald-300 border border-emerald-600/40 rounded px-2 py-0.5"
                  title="Download the raw JSON"
                >
                  ⤓ Download
                </a>
              </>
            )}
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-200 text-xl px-2"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-3 bg-black/30">
          {err ? (
            <p className="text-xs text-red-400">✗ {err}</p>
          ) : data == null ? (
            <p className="text-xs text-slate-500 animate-pulse">Loading…</p>
          ) : (
            <pre className="text-[11px] text-slate-300 font-mono whitespace-pre-wrap break-all leading-relaxed">
              {pretty}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}


function CropActions({ path, filename, jobId }: {
  path: string;
  filename: string;
  jobId?: string;
}) {
  const [trace, setTrace] = useState<CropTrace | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  // Tracks whether we've kicked off a trace this session. Used to detect the
  // "worker crashed before writing anything" case — the API keeps returning
  // {status: "none"} and we would otherwise silently revert to Find points.
  const [queued, setQueued] = useState(false);
  const [ghostError, setGhostError] = useState<string | null>(null);
  const startedAt = useRef<number | null>(null);

  // Category the tracer WILL use on the next Find Points click.  Pre-loads
  // via /effective-category so the tile shows the current pick + source
  // without waiting for the user to open the modal.
  const [effCat, setEffCat] = useState<{ category: string | null; source: string } | null>(null);
  useEffect(() => {
    if (!jobId) { setEffCat(null); return; }
    api.batch.getPanelEffectiveCategory(path, jobId, filename)
      .then(setEffCat)
      .catch(() => setEffCat(null));
  }, [path, jobId, filename]);

  const changeCategory = async (c: string) => {
    if (!jobId) return;
    try {
      const r = await api.batch.patchPanelCategory(path, jobId, filename, c);
      setEffCat({ category: r.category, source: r.source as never });
    } catch (e) {
      setGhostError(e instanceof Error ? e.message : String(e));
    }
  };

  // Wipe the panel's OCR JSON — anchors, ticks, curve names, category —
  // so the next trace starts from scratch.  Used when a bad manual setup
  // is producing wrong curves and the operator wants to redo it (usually
  // by AI or the wizard).  Confirm-first because it's destructive.
  const clearAxes = async () => {
    if (!jobId) return;
    if (!window.confirm(
        "Wipe all manual axes + curve names for this panel?  The category will reset to auto and the next Retrace will start from a clean state.")) {
      return;
    }
    try {
      await api.batch.correctOcr(path, jobId, filename, [], [], [],
        { xPositions: [], yPositions: [],
          xPositionsPy: [], yPositionsPx: [],
          hLabels: [], vLabels: [], category: "" });
      const fresh = await api.batch.getPanelEffectiveCategory(path, jobId, filename);
      setEffCat(fresh);
    } catch (e) {
      setGhostError(`Clear failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // AI-analyzer state: result of the last 🤖 click, plus a running flag.
  // Shown as a small "🤖 last: $0.0012 · 850 tok · gpt-4o-mini" badge below
  // the action buttons so the operator can see per-call cost.
  const [aiBusy, setAiBusy] = useState(false);
  const [aiInfo, setAiInfo] = useState<{
    model: string; inTok: number; outTok: number; cost: number;
    name: string | null; category: string | null;
  } | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [showJson, setShowJson] = useState(false);

  const askAi = async () => {
    if (!jobId) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const r = await api.batch.aiAnalyzePanel(path, jobId, filename);
      setAiInfo({
        model: r.model_name ?? "?",
        inTok: r.input_tokens,
        outTok: r.output_tokens,
        cost: r.cost_usd,
        name: r.name,
        category: r.category,
      });
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const t = jobId
        ? await api.batch.tracePanelStatus(path, jobId, filename)
        : await api.batch.traceStatus(path, filename);
      setTrace(t);
      setLoaded(true);
      // If we kicked one off in this session, the API should have a record.
      // No record within a few seconds means the worker crashed before its
      // first write — surface it instead of silently reverting.
      if (queued && (!t || t.status === "none")) {
        // Bump to 60 s: task_trace_crop + custom handlers (dual_carpet
        // upscales small crops to 1700 wide then runs slow morphological
        // openings) routinely takes ~35 s.  A 5-s window was firing this
        // ghost error on healthy tasks.  The endpoint also writes a
        // "queued" stub as soon as apply_async is called so we should
        // basically never hit this branch now — kept as a safety net.
        const elapsed = startedAt.current ? Date.now() - startedAt.current : 0;
        if (elapsed > 60000) {
          setGhostError(
            "The tracing worker did not write any status — it likely crashed before starting. Check the worker container logs.",
          );
          setQueued(false);
        }
      }
    } catch (e) {
      setLoaded(true);
      // A crop that has never been traced is a 404, which we treat as "none".
      // Real API errors (5xx, network) are worth surfacing.
      if (queued) {
        setGhostError(`Status probe failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    }
  }, [path, filename, jobId, queued]);

  useEffect(() => { void load(); }, [load]);

  // Poll only while the worker is actually running (or we're waiting for it
  // to even show up), and stop the moment we have an answer.
  useEffect(() => {
    if (trace?.status !== "running" && !queued) return;
    if (ghostError) return;
    const t = setInterval(() => void load(), 1500);
    return () => clearInterval(t);
  }, [trace?.status, queued, ghostError, load]);

  const findPoints = async () => {
    setBusy(true);
    setGhostError(null);
    try {
      if (jobId) await api.batch.tracePanel(path, jobId, filename);
      else await api.batch.traceCrop(path, filename);
      setTrace({ status: "running" });
      setQueued(true);
      startedAt.current = Date.now();
    } catch (e) {
      setGhostError(`Failed to queue trace: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const retrace = async () => {
    setBusy(true);
    setGhostError(null);
    try {
      // Wipe the stored trace first so the UI reflects a true reset and any
      // stale WPD project the operator might otherwise open is gone.
      if (jobId) await api.batch.deletePanelTrace(path, jobId, filename);
      else       await api.batch.deleteTrace(path, filename);
      // Then queue a fresh run — same as clicking Find points from scratch.
      if (jobId) await api.batch.tracePanel(path, jobId, filename);
      else       await api.batch.traceCrop(path, filename);
      setTrace({ status: "running" });
      setQueued(true);
      startedAt.current = Date.now();
    } catch (e) {
      setGhostError(`Retrace failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const openInWpd = async () => {
    // Open the tab *synchronously* inside the click handler — after any
    // `await` the browser stops treating window.open as user-initiated and
    // silently blocks it. We then redirect the placeholder tab once the
    // review + hitl-init round-trips finish.
    const win = window.open("about:blank", "_blank");
    if (!win) {
      alert("Popup blocked — allow popups for this site and try again.");
      return;
    }
    setBusy(true);
    try {
      // Retrace first so the WPD project reflects the effective category
      // (dropdown / folder / filename auto-detect).  Bail out with a
      // human-readable alert if the trace endpoint rejects for lack of a
      // resolvable chart type — silently falling through would leave the
      // Chart row with stale or empty datasets and the operator wondering
      // why WPD looks wrong.
      if (jobId) {
        try {
          await api.batch.deletePanelTrace(path, jobId, filename);
        } catch { /* nothing to delete is fine */ }
        try {
          await api.batch.tracePanel(path, jobId, filename);
        } catch (e) {
          win.close();
          const msg = e instanceof Error ? e.message : String(e);
          alert(`Cannot open in WPD:\n${msg}`);
          return;
        }
        // Poll until the async trace finishes so the Chart row picks up
        // the fresh curves.  Max ~30 s; every second we re-read status.
        for (let i = 0; i < 30; i++) {
          await new Promise(res => setTimeout(res, 1000));
          try {
            const s = await api.batch.tracePanelStatus(path, jobId, filename);
            if (s.status === "done") break;
            if (s.status === "failed") {
              win.close();
              alert(`Trace failed:\n${s.error ?? "unknown error"}`);
              return;
            }
          } catch { /* transient — keep polling */ }
        }
      }
      // Two steps on purpose. `reviewCrop` promotes the crop into a Chart —
      // the one point where the S3-only batch world meets the pipeline's
      // tables — and `hitlInit` is the existing endpoint that builds the WPD
      // project and hands back a URL already carrying its session, chart and
      // job parameters. Constructing that URL here would duplicate knowledge
      // the wrapper page and the router already share.
      const r = await api.batch.reviewCrop(path, filename, jobId);
      const init = await api.hitl.init(r.job_id, r.chart_id);
      win.location.href = init.wpd_editor_url;
    } catch (e) {
      win.close();
      alert(`Failed to open in WPD: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const status = ghostError ? "failed" : (trace?.status ?? "none");
  const errorMessage = ghostError ?? trace?.error;

  // Show neutral loading indicator until we know the real state — prevents
  // a new user from seeing "Find points" and clicking it on an already-traced panel.
  if (!loaded && !queued) {
    return (
      <div className="px-1.5 pb-1.5">
        <div className="w-full text-[10px] text-slate-600 text-center py-1.5 animate-pulse">
          checking…
        </div>
      </div>
    );
  }

  // Compact chart-type picker.  Rendered above every status branch so
  // an operator can review or override the category before Retrace,
  // Try again, or Find Points fires.
  const categoryPicker = jobId ? (
    <div className="flex items-center gap-1">
      <select
        value={effCat?.source === "manual" ? (effCat?.category ?? "") : ""}
        onChange={(e) => void changeCategory(e.target.value)}
        className={`flex-1 text-[10px] bg-rdp-bg border rounded px-1 py-0.5 ${
          effCat?.source === "manual"
            ? "border-emerald-600/60 text-emerald-300"
            : effCat?.category
              ? "border-amber-600/50 text-amber-300"
              : "border-red-700/60 text-red-400"
        }`}
        title={
          effCat?.source === "manual"
            ? "Manual override — takes priority"
            : effCat?.category
              ? `Auto (${effCat.source}): tracer will use "${effCat.category}"`
              : "No chart type detected — trace will fail. Pick one here."
        }
      >
        {TILE_CATEGORIES.map(c => {
          const label = c.id === "" && effCat?.category && effCat?.source !== "manual"
            ? `— auto: ${effCat.category} (${effCat.source}) —`
            : c.label;
          return <option key={c.id} value={c.id}>{label}</option>;
        })}
      </select>
    </div>
  ) : null;

  return (
    <div className="px-1.5 pb-1.5 space-y-1">
      {categoryPicker}
      {status === "done" ? (
        <>
          <div className="flex items-center gap-1 text-[9px] flex-wrap">
            <span className="text-emerald-400">✓ {trace?.curve_count ?? 0} curves</span>
            {trace?.tuned === false && (
              <span className="text-amber-400" title={trace?.rules}>· untuned</span>
            )}
            {trace?.numbers_read === false && (
              <span className="text-slate-600" title="Number reading is not built yet">
                · numbers pending
              </span>
            )}
          </div>
          <button
            onClick={openInWpd}
            disabled={busy}
            className="w-full text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-400 rounded py-1.5 shadow-sm"
          >
            {busy ? "Opening…" : "Open in WPD →"}
          </button>
          <button
            onClick={retrace}
            disabled={busy}
            title="Delete this trace and re-run point-finding from scratch"
            className="w-full text-[10px] text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded py-1"
          >
            ↻ Retrace
          </button>
          <button
            onClick={clearAxes}
            disabled={busy}
            title="Wipe manual anchors + ticks + curve names + category so the next attempt starts from a clean slate"
            className="w-full text-[10px] text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded py-1"
          >
            🗑 Clear axes
          </button>
        </>
      ) : status === "running" ? (
        <div className="text-[10px] text-blue-300 py-1 text-center">Finding points…</div>
      ) : status === "failed" ? (
        <>
          <p className="text-[10px] text-red-400 leading-tight" title={errorMessage}>
            ✕ {errorMessage ?? "failed"}
          </p>
          <button
            onClick={() => { setGhostError(null); void findPoints(); }}
            disabled={busy}
            className="w-full text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-400 rounded py-1.5 shadow-sm"
          >
            {busy ? "Queuing…" : "Try again"}
          </button>
        </>
      ) : (
        <button
          onClick={findPoints}
          disabled={busy}
          className="w-full text-xs font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-400 rounded py-1.5 shadow-sm"
        >
          {busy ? "Queuing…" : "◎ Find points"}
        </button>
      )}

      {/* AI analyzer — one click sends the crop to the configured cheap
          vision model, applies name + category + hints back into the panel's
          OCR JSON, and reports tokens + $ so the operator sees per-call cost. */}
      {jobId && (
        <div className="pt-1 space-y-1">
          <div className="flex items-center gap-1">
            <button
              onClick={askAi}
              disabled={aiBusy || busy}
              title="Ask the configured chart_analyze LLM to name this chart, pick its category and read the axes / curve labels"
              className="flex-1 text-[11px] font-medium text-purple-100 bg-purple-700/70 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-400 rounded py-1"
            >
              {aiBusy ? "🤖 Analysing…" : "🤖 Ask AI"}
            </button>
            <button
              onClick={() => setShowJson(true)}
              title="Show the raw AI response and the merged OCR JSON stored for this panel"
              className="text-[11px] text-slate-400 hover:text-slate-200 border border-slate-600 rounded px-1.5 py-1"
            >
              🔎 JSON
            </button>
          </div>
          {aiInfo && (
            <p
              className="text-[9px] text-purple-300 leading-tight truncate"
              title={`Suggested: ${aiInfo.name ?? "?"} · category ${aiInfo.category ?? "?"} · ${aiInfo.model}`}
            >
              🤖 ${aiInfo.cost.toFixed(4)} · {aiInfo.inTok + aiInfo.outTok} tok
              {aiInfo.category && <span className="text-emerald-400"> · {aiInfo.category}</span>}
            </p>
          )}
          {aiError && (
            <p className="text-[9px] text-red-400 leading-tight" title={aiError}>
              🤖 ✗ {aiError}
            </p>
          )}
        </div>
      )}

      {showJson && (
        <OcrJsonViewer
          path={path}
          jobId={jobId}
          filename={filename}
          onClose={() => setShowJson(false)}
        />
      )}
    </div>
  );
}


export function PanelCrop() {
  const [path, setPath] = useState("");            // "" = root
  const [data, setData] = useState<BatchContents>(EMPTY);
  const [loading, setLoading] = useState(false);

  const [files, setFiles] = useState<Picked[]>([]);
  const dirInputRef = useRef<HTMLInputElement>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [processing, setProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [newFolder, setNewFolder] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<{ job: BatchJob; panel: BatchPanel } | null>(null);

  const [log, setLog] = useState<LogEntry[]>([]);
  const [runStart, setRunStart] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const logRef = useRef<HTMLDivElement>(null);
  const logId = useRef(0);
  // Lets "Stop watching" break the poll loop; the workers carry on regardless.
  const cancelRef = useRef(false);

  const addLog = useCallback((kind: LogEntry["kind"], text: string) => {
    logId.current += 1;
    setLog((prev) => {
      const next = [...prev, { id: logId.current, time: clock(), kind, text }];
      // A 300-file run would otherwise grow the DOM unboundedly.
      return next.length > 600 ? next.slice(-600) : next;
    });
  }, []);

  // Keep the newest line visible while a run is in flight.
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log]);

  // Drives the elapsed clock; only ticks while something is running.
  useEffect(() => {
    if (runStart === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [runStart]);

  const refresh = useCallback(async (p: string) => {
    setLoading(true);
    try {
      if (p) {
        setData(await api.batch.contents(p));
      } else {
        const r = await api.batch.listFolders();
        setData({ ...EMPTY, folders: r.folders });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load folder");
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh(path);
    setExpanded(null);
  }, [path, refresh]);

  const crumbs = path ? path.split("/") : [];

  const handleCreateFolder = async () => {
    const name = newFolder.trim();
    if (!name) return;
    setBusy("folder");
    setError(null);
    try {
      await api.batch.createFolder(name, path || undefined);
      setNewFolder("");
      await refresh(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create folder");
    } finally {
      setBusy(null);
    }
  };

  const handleDeleteFolder = async (f: BatchFolder) => {
    if (
      !window.confirm(
        `Delete folder "${f.name}" and everything inside it? This cannot be undone.`
      )
    )
      return;
    setError(null);
    try {
      await api.batch.deleteFolder(f.path);
      await refresh(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete folder");
    }
  };

  const handleExtract = async (job: BatchJob) => {
    setBusy(job.job_id);
    setError(null);
    try {
      await api.batch.extractToFolder(path, job.job_id);
      await refresh(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not extract to folder");
    } finally {
      setBusy(null);
    }
  };

  // Grouping is derived from filenames and can't be perfect, so always preview
  // first and let the user confirm before anything is written.
  const handleCategorize = async () => {
    setBusy("categorize");
    setError(null);
    try {
      const preview = await api.batch.categorize(path, true);
      const top = preview.groups
        .slice(0, 12)
        .map((g) => `  ${String(g.panel_count).padStart(3)} charts — ${g.name}`)
        .join("\n");
      const more = preview.groups.length > 12 ? `\n  … and ${preview.groups.length - 12} more` : "";
      const totalCharts = preview.groups.reduce((n, g) => n + g.panel_count, 0);
      const unnamed = preview.panels_without_a_name ?? 0;

      if (
        !window.confirm(
          `Create ${preview.groups.length} folders, one per chart name, ` +
            `holding ${totalCharts} charts?\n\n${top}${more}\n\n` +
            (unnamed
              ? `${unnamed} chart(s) have no labelled name and fall back to the ` +
                `PDF filename.\n\n`
              : "") +
            `Each folder also gets a metadata.json describing its charts. ` +
            `The original PDFs are left untouched.`
        )
      )
        return;

      const res = await api.batch.categorize(path, false);
      addLog(
        "info",
        `Categorised: ${res.folders_created} folders, ${res.charts_copied} charts`
      );
      await refresh(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not categorize");
    } finally {
      setBusy(null);
    }
  };

  // Re-runs detection with whatever weights are deployed now. Names and axis
  // calibration are re-matched onto the new boxes server-side.
  const handleRedetect = async () => {
    if (
      !window.confirm(
        `Re-run detection on all ${data.jobs.length} PDF(s) with the current model?\n\n` +
          `Existing crops are replaced. Chart names and axis calibration are kept ` +
          `and re-matched to the new boxes.\n\n` +
          `This runs in the background — you can leave the page.`
      )
    )
      return;
    setBusy("redetect");
    setError(null);
    try {
      const r = await api.batch.redetect(path);
      addLog("info", `Re-detection queued for ${r.queued} PDF(s)`);
      await refresh(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start re-detection");
    } finally {
      setBusy(null);
    }
  };

  const handleExtractAll = async () => {
    const pending = data.jobs.filter((j) => !j.extracted_to && j.total_panels > 0);
    if (pending.length === 0) return;
    if (!window.confirm(`Create ${pending.length} folder(s), one per PDF?`)) return;
    setBusy("all");
    setError(null);
    try {
      for (const j of pending) await api.batch.extractToFolder(path, j.job_id);
      await refresh(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not extract all");
    } finally {
      setBusy(null);
    }
  };

  const handleDeleteJob = async (job: BatchJob) => {
    if (!window.confirm(`Delete "${job.filename}" and its ${job.total_panels} panel(s)?`))
      return;
    setError(null);
    try {
      await api.batch.deleteJob(path, job.job_id);
      await refresh(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete");
    }
  };

  const handleDeletePanel = async (job: BatchJob, name: string) => {
    if (!window.confirm(`Delete panel "${name}"?`)) return;
    try {
      await api.batch.deletePanel(path, job.job_id, name);
      await refresh(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete panel");
    }
  };

  const handleDeleteImage = async (name: string) => {
    if (!window.confirm(`Delete image "${name}"?`)) return;
    try {
      await api.batch.deleteImage(path, name);
      await refresh(path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete image");
    }
  };

  // `webkitdirectory` isn't in React's prop types, so set it on the DOM node.
  useEffect(() => {
    const el = dirInputRef.current;
    if (el) {
      el.setAttribute("webkitdirectory", "");
      el.setAttribute("directory", "");
    }
  }, []);

  const addPicked = (picked: Picked[]) => {
    if (picked.length === 0) return;
    setFiles((prev) => {
      const seen = new Set(prev.map((p) => `${p.dir}/${p.file.name}`));
      return [...prev, ...picked.filter((p) => !seen.has(`${p.dir}/${p.file.name}`))];
    });
  };

  /** Plain file input — `webkitRelativePath` is set when a folder was chosen. */
  const addFromInput = (list: FileList) => {
    const picked: Picked[] = [];
    for (const file of Array.from(list)) {
      if (!isPdf(file.name)) continue;
      const rel = file.webkitRelativePath || "";
      const dir = rel.includes("/") ? rel.slice(0, rel.lastIndexOf("/")) : "";
      picked.push({ file, dir: sanitizeDir(dir) });
    }
    addPicked(picked);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    // Capture entries synchronously — the DataTransfer is cleared after await.
    const entries = Array.from(e.dataTransfer.items)
      .map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
      .filter(Boolean);

    if (entries.length === 0) {
      addPicked(
        Array.from(e.dataTransfer.files)
          .filter((f) => isPdf(f.name))
          .map((file) => ({ file, dir: "" }))
      );
      return;
    }

    const out: Picked[] = [];
    for (const entry of entries) await walkEntry(entry, "", out);
    addPicked(out.map((p) => ({ ...p, dir: sanitizeDir(p.dir) })));
  };

  const handleProcess = useCallback(async () => {
    if (!path || files.length === 0) return;
    const total = files.length;
    setProcessing(true);
    setError(null);
    setUploads(files.map((f) => ({ name: key(f), status: "queued" as const })));
    const startedAt = Date.now();
    setRunStart(startedAt);
    setNow(startedAt);
    cancelRef.current = false;
    addLog("info", `Uploading ${total} PDF${total !== 1 ? "s" : ""} into "${path}"`);

    // Recreate the picked folder structure before uploading into it.
    const dirs = [...new Set(files.map((f) => f.dir).filter(Boolean))].sort();
    for (const d of dirs) {
      try {
        const r = await api.batch.ensureFolder(`${path}/${d}`);
        if (r.created.length > 0) addLog("info", `Created folder ${d}`);
      } catch (e) {
        addLog("fail", `Could not create folder ${d} — ${e instanceof Error ? e.message : ""}`);
      }
    }

    // Phase 1 — hand every PDF to the server. Each call only stores the file
    // and enqueues a task, so this is fast regardless of detection cost.
    const tracked = new Map<string, { label: string; target: string }>();
    let bad = 0;

    for (const [i, picked] of files.entries()) {
      if (cancelRef.current) break;
      const { file, dir } = picked;
      const target = dir ? `${path}/${dir}` : path;
      const label = key(picked);
      try {
        const r = await api.batch.cropPDF(target, file, () => {});
        tracked.set(r.job_id, { label, target });
      } catch (e) {
        bad += 1;
        const msg = e instanceof Error ? e.message : "Failed";
        setUploads((p) => p.map((u) => (u.name === label ? { ...u, status: "failed", error: msg } : u)));
        addLog("fail", `[${i + 1}/${total}] ${label} — ${msg}`);
      }
    }

    setFiles([]);
    addLog("info", `${tracked.size} queued — workers are processing (2 at a time)`);

    // Phase 2 — watch the workers. Job state lives in each job's meta.json, so
    // one poll per target folder covers every file in it.
    const targets = [...new Set([...tracked.values()].map((t) => t.target))];
    const lastSeen = new Map<string, string>();

    while (!cancelRef.current) {
      await new Promise((r) => setTimeout(r, POLL_MS));

      const byId = new Map<string, BatchJob>();
      for (const t of targets) {
        try {
          const d = await api.batch.contents(t);
          for (const j of d.jobs) byId.set(j.job_id, j);
        } catch {
          // Transient — just try again on the next tick.
        }
      }

      let pending = 0;
      for (const [jobId, info] of tracked) {
        const j = byId.get(jobId);
        if (!j) continue; // deleted mid-run
        if (j.status !== lastSeen.get(jobId)) {
          lastSeen.set(jobId, j.status);
          if (j.status === "processing") {
            addLog("start", info.label);
            setUploads((p) =>
              p.map((u) => (u.name === info.label ? { ...u, status: "processing" } : u))
            );
          } else if (j.status === "completed") {
            addLog(
              "done",
              `${info.label} — ${j.total_panels} panel${j.total_panels !== 1 ? "s" : ""}` +
                `${j.detector === "full-page" ? " (no model)" : ""}`
            );
            setUploads((p) =>
              p.map((u) =>
                u.name === info.label ? { ...u, status: "done", panels: j.total_panels } : u
              )
            );
          } else if (j.status === "failed") {
            bad += 1;
            addLog("fail", `${info.label} — ${j.error ?? "worker failed"}`);
            setUploads((p) =>
              p.map((u) =>
                u.name === info.label
                  ? { ...u, status: "failed", error: j.error ?? "worker failed" }
                  : u
              )
            );
          }
        }
        if (j.status === "queued" || j.status === "processing") pending += 1;
      }

      if (pending === 0) break;
      if (targets.includes(path)) await refresh(path);
    }

    const ok = [...lastSeen.values()].filter((s) => s === "completed").length;
    addLog(
      "info",
      cancelRef.current
        ? "Stopped watching — workers keep going in the background"
        : `Finished: ${ok} succeeded, ${bad} failed, total ${formatDuration(
            Date.now() - startedAt
          )}`
    );

    cancelRef.current = false;
    setProcessing(false);
    setRunStart(null);
    await refresh(path);
  }, [path, files, refresh, addLog]);

  const doneCount = uploads.filter((u) => u.status === "done").length;
  const failCount = uploads.filter((u) => u.status === "failed").length;
  const settled = doneCount + failCount;
  const elapsed = runStart ? now - runStart : 0;
  const eta =
    runStart && settled > 0 && settled < uploads.length
      ? ((now - runStart) / settled) * (uploads.length - settled)
      : null;
  const pendingExtract = data.jobs.filter((j) => !j.extracted_to && j.total_panels > 0).length;

  return (
    <>
    <div className="flex flex-col h-full bg-rdp-bg">
      <div className="px-5 py-3 border-b border-rdp-border bg-rdp-card/30">
        <h2 className="text-lg font-semibold text-white">Batch Panel Crop</h2>
        <p className="text-xs text-slate-500 mt-1">
          Detect and crop chart panels, organised into nested folders
        </p>
      </div>

      <div className="flex-1 overflow-auto flex flex-col gap-4 p-5">
        {error && (
          <div className="flex items-start justify-between gap-3 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            <p className="text-xs text-red-400">{error}</p>
            <button onClick={() => setError(null)} className="text-red-400/60 hover:text-red-300 text-xs">
              ✕
            </button>
          </div>
        )}

        {/* ── Breadcrumbs ─────────────────────────────────────────────── */}
        <div className="flex items-center gap-1.5 text-xs flex-wrap">
          <button
            onClick={() => setPath("")}
            className={path ? "text-blue-400 hover:text-blue-300" : "text-white font-medium"}
          >
            🗂 All folders
          </button>
          {crumbs.map((c, i) => {
            const target = crumbs.slice(0, i + 1).join("/");
            const last = i === crumbs.length - 1;
            return (
              <span key={target} className="flex items-center gap-1.5">
                <span className="text-slate-600">/</span>
                <button
                  onClick={() => setPath(target)}
                  className={last ? "text-white font-medium" : "text-blue-400 hover:text-blue-300"}
                >
                  {c}
                </button>
              </span>
            );
          })}
          {path && (
            <span className="ml-auto flex items-center gap-3">
              {data.jobs.length > 0 && (
                <button
                  onClick={handleRedetect}
                  disabled={busy === "redetect"}
                  className="text-blue-400 hover:text-blue-300 disabled:text-slate-600"
                  title="Re-run detection on every PDF here with the current model, keeping chart names"
                >
                  {busy === "redetect" ? "Queuing…" : "🔄 Re-detect"}
                </button>
              )}
              {data.jobs.length > 0 && (
                <button
                  onClick={handleCategorize}
                  disabled={busy === "categorize"}
                  className="text-blue-400 hover:text-blue-300 disabled:text-slate-600"
                  title="Group these PDFs into one folder per chart name, each with a metadata.json"
                >
                  {busy === "categorize" ? "Categorising…" : "🗃 Categorize"}
                </button>
              )}
              <a
                href={api.batch.datasetUrl(path)}
                onClick={() => addLog("info", "Building fine-tuning dataset — every page is re-rendered, so this one takes a few minutes")}
                className="text-blue-400 hover:text-blue-300"
                title="Export pages + predicted boxes as a YOLO dataset to correct and fine-tune on"
              >
                🎯 Dataset
              </a>
              <a
                href={api.batch.downloadUrl(path)}
                onClick={() => addLog("info", "Preparing ZIP — the browser will show it shortly; large folders take a few minutes to finish downloading")}
                className="text-blue-400 hover:text-blue-300"
                title="Download this folder as ZIP. Large folders take a few minutes to transfer."
              >
                📥 ZIP
              </a>
            </span>
          )}
        </div>

        {/* ── New folder ──────────────────────────────────────────────── */}
        <div className="flex gap-2">
          <input
            value={newFolder}
            onChange={(e) => setNewFolder(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
            placeholder={path ? `New folder inside "${crumbs[crumbs.length - 1]}"…` : "New folder name…"}
            maxLength={64}
            className="flex-1 bg-rdp-hover border border-rdp-border rounded px-2.5 py-1.5 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
          />
          <button
            onClick={handleCreateFolder}
            disabled={busy === "folder" || !newFolder.trim()}
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              busy === "folder" || !newFolder.trim() ? "btn-disabled" : "btn-primary"
            }`}
          >
            + Create
          </button>
        </div>

        {/* ── Folders ─────────────────────────────────────────────────── */}
        {loading ? (
          <p className="text-xs text-slate-600">Loading…</p>
        ) : (
          <>
            {data.folders.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1">
                {data.folders.map((f) => {
                  const items =
                    (f.folder_count ?? 0) + (f.job_count ?? 0) || undefined;
                  return (
                    <div key={f.path} className="group relative">
                      <button
                        onDoubleClick={() => setPath(f.path)}
                        onClick={() => setPath(f.path)}
                        title={f.name}
                        className="w-full flex flex-col items-center gap-1 rounded-lg p-2 hover:bg-blue-500/10 focus:bg-blue-500/15 focus:outline-none transition-colors"
                      >
                        <FolderIcon className="w-14 h-11 drop-shadow-sm" />
                        <span className="text-[11px] text-slate-200 leading-tight text-center line-clamp-2 break-all">
                          {f.name}
                        </span>
                        <span className="text-[10px] text-slate-500 leading-none">
                          {items === undefined
                            ? "—"
                            : `${items} item${items !== 1 ? "s" : ""}`}
                        </span>
                      </button>
                      <button
                        onClick={() => handleDeleteFolder(f)}
                        title="Delete folder"
                        className="absolute top-1 right-1 rounded bg-black/60 px-1 text-[10px] text-slate-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        🗑
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {!path ? (
              data.folders.length === 0 && (
                <p className="text-xs text-slate-600">
                  No folders yet — create one above to start uploading PDFs.
                </p>
              )
            ) : (
              <>
                {/* ── Upload ──────────────────────────────────────────── */}
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors ${
                    dragActive
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-rdp-border bg-rdp-card hover:bg-rdp-hover"
                  }`}
                >
                  <input
                    type="file"
                    multiple
                    accept=".pdf"
                    onChange={(e) => {
                      if (e.target.files) addFromInput(e.target.files);
                      e.target.value = "";
                    }}
                    className="hidden"
                    id="file-input"
                  />
                  <input
                    ref={dirInputRef}
                    type="file"
                    multiple
                    onChange={(e) => {
                      if (e.target.files) addFromInput(e.target.files);
                      e.target.value = "";
                    }}
                    className="hidden"
                    id="dir-input"
                  />
                  <div className="text-2xl mb-1">📄</div>
                  <p className="text-sm font-medium text-white">
                    Drop PDFs <span className="text-slate-500">or a whole folder</span> here
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 mb-3">
                    into <span className="text-slate-300">{path}</span>
                  </p>
                  <div className="flex items-center justify-center gap-2">
                    <label
                      htmlFor="file-input"
                      className="cursor-pointer rounded border border-rdp-border bg-rdp-hover px-3 py-1.5 text-xs text-slate-200 hover:border-blue-500 transition-colors"
                    >
                      Choose files
                    </label>
                    <label
                      htmlFor="dir-input"
                      className="cursor-pointer rounded border border-rdp-border bg-rdp-hover px-3 py-1.5 text-xs text-slate-200 hover:border-blue-500 transition-colors"
                    >
                      📁 Choose folder
                    </label>
                  </div>
                </div>

                {files.length > 0 && (
                  <div className="bg-rdp-card rounded-lg border border-rdp-border p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-slate-300">
                        {files.length} file{files.length !== 1 ? "s" : ""} ready
                        {(() => {
                          const n = new Set(files.map((f) => f.dir).filter(Boolean)).size;
                          return n > 0 ? (
                            <span className="text-slate-500 font-normal">
                              {" "}
                              · {n} subfolder{n !== 1 ? "s" : ""} will be created
                            </span>
                          ) : null;
                        })()}
                      </span>
                      <button
                        onClick={() => setFiles([])}
                        className="text-xs text-slate-600 hover:text-slate-400"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="max-h-32 overflow-auto space-y-1 mb-3">
                      {files.map((p, idx) => (
                        <div
                          key={`${key(p)}-${idx}`}
                          className="flex items-center justify-between text-xs text-slate-400 p-1.5 rounded bg-rdp-hover/50"
                        >
                          <span className="flex items-center gap-1.5 min-w-0">
                            <PdfIcon className="w-3.5 h-4 flex-shrink-0" />
                            <span className="truncate">
                              {p.dir && <span className="text-slate-600">{p.dir}/</span>}
                              {p.file.name}
                            </span>
                          </span>
                          <button
                            onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-slate-600 hover:text-red-400 ml-2"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={handleProcess}
                      disabled={processing}
                      className={`w-full py-2 px-3 rounded-lg font-medium text-sm ${
                        processing ? "btn-disabled" : "btn-primary"
                      }`}
                    >
                      {processing
                        ? `Processing ${doneCount + 1} of ${uploads.length}…`
                        : `Process ${files.length} PDF${files.length !== 1 ? "s" : ""}`}
                    </button>
                    {files.length > 20 && !processing && (
                      <p className="text-[10px] text-amber-500/80 mt-2">
                        ~{Math.ceil((files.length * 4) / 60)} min estimated. Finished PDFs are
                        saved as they complete — you can safely stop and resume later.
                      </p>
                    )}
                  </div>
                )}

                {uploads.length > 0 && (
                  <div className="bg-rdp-card rounded-lg border border-rdp-border p-3">
                    {/* Counters */}
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-slate-300 font-medium">
                          {settled} / {uploads.length}
                        </span>
                        <span className="text-emerald-400">✓ {doneCount} done</span>
                        {failCount > 0 && <span className="text-red-400">✗ {failCount} failed</span>}
                        <span className="text-slate-500">
                          {uploads.length - settled} remaining
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500">
                        {runStart !== null && <span>elapsed {formatDuration(elapsed)}</span>}
                        {eta !== null && <span>~{formatDuration(eta)} left</span>}
                        {processing ? (
                          <button
                            onClick={() => {
                              cancelRef.current = true;
                            }}
                            className="text-slate-600 hover:text-slate-400"
                            title="Stop watching. Workers keep processing in the background."
                          >
                            Stop watching
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setUploads([]);
                              setLog([]);
                            }}
                            className="text-slate-600 hover:text-slate-400"
                          >
                            Dismiss
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="h-1.5 w-full rounded-full bg-rdp-hover overflow-hidden mb-3">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${(settled / uploads.length) * 100}%` }}
                      />
                    </div>

                    {/* Live log */}
                    <div
                      ref={logRef}
                      className="max-h-48 overflow-auto rounded bg-black/40 border border-rdp-border p-2 font-mono text-[11px] leading-relaxed"
                    >
                      {log.map((l) => (
                        <div key={l.id} className="flex gap-2">
                          <span className="text-slate-700 flex-shrink-0">{l.time}</span>
                          <span
                            className={
                              l.kind === "done"
                                ? "text-emerald-400"
                                : l.kind === "fail"
                                ? "text-red-400"
                                : l.kind === "start"
                                ? "text-blue-400"
                                : "text-slate-500"
                            }
                          >
                            {l.kind === "done"
                              ? "✓"
                              : l.kind === "fail"
                              ? "✗"
                              : l.kind === "start"
                              ? "▶"
                              : "•"}
                          </span>
                          <span className="text-slate-400 break-all">{l.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Loose images ────────────────────────────────────── */}
                {data.images.length > 0 && (
                  <div className="bg-rdp-card rounded-lg border border-rdp-border p-3">
                    <div className="text-xs font-medium text-slate-300 mb-2">
                      {data.images.length} chart{data.images.length !== 1 ? "s" : ""} in this folder
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {data.images.map((img) => (
                        <div
                          key={img.name}
                          className="group relative border border-rdp-border rounded overflow-hidden"
                        >
                          <a href={img.url} target="_blank" rel="noreferrer">
                            <img
                              src={img.url}
                              alt={img.name}
                              loading="lazy"
                              className="w-full h-24 object-contain bg-black/30"
                            />
                          </a>
                          <button
                            onClick={() => handleDeleteImage(img.name)}
                            title="Delete this chart"
                            className="absolute top-1 right-1 bg-black/70 rounded px-1.5 py-0.5 text-[10px] text-slate-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            🗑
                          </button>
                          <p className="px-1.5 py-1 text-[10px] text-slate-400 truncate">
                            {img.name}
                          </p>
                          <CropActions path={path} filename={img.name} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── PDFs ────────────────────────────────────────────── */}
                <div className="bg-rdp-card rounded-lg border border-rdp-border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-300">
                      {data.jobs.length} PDF{data.jobs.length !== 1 ? "s" : ""}
                    </span>
                    {pendingExtract > 1 && (
                      <button
                        onClick={handleExtractAll}
                        disabled={busy === "all"}
                        className="text-xs text-blue-400 hover:text-blue-300 disabled:text-slate-600"
                      >
                        {busy === "all" ? "Creating…" : `📁 Folder for each (${pendingExtract})`}
                      </button>
                    )}
                  </div>

                  {data.jobs.length === 0 ? (
                    <p className="text-xs text-slate-600">No PDFs here yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {data.jobs.map((job) => {
                        const open = expanded === job.job_id;
                        return (
                          <div
                            key={job.job_id}
                            className="rounded bg-rdp-hover/50 border border-rdp-border"
                          >
                            <div className="flex items-center justify-between p-2.5 text-xs">
                              <button
                                onClick={() => setExpanded(open ? null : job.job_id)}
                                className="flex-1 min-w-0 text-left flex items-center gap-2"
                              >
                                <span className="text-slate-600 flex-shrink-0">
                                  {open ? "▾" : "▸"}
                                </span>
                                <PdfIcon className="w-5 h-6 flex-shrink-0" />
                                <span className="min-w-0">
                                <JobNameEdit
                                  jobName={job.filename}
                                  onRename={async (next) => {
                                    try {
                                      await api.batch.renameJob(path, job.job_id, next);
                                      await refresh(path);
                                    } catch (e) {
                                      window.alert(`Rename failed: ${e instanceof Error ? e.message : String(e)}`);
                                    }
                                  }}
                                />
                                <p className="text-[10px] mt-0.5">
                                  {job.status === "queued" && (
                                    <span className="text-slate-500">⏳ queued</span>
                                  )}
                                  {job.status === "processing" && (
                                    <span className="text-blue-400">● detecting…</span>
                                  )}
                                  {job.status === "failed" && (
                                    <span className="text-red-400">
                                      ✗ {job.error ?? "failed"}
                                    </span>
                                  )}
                                  {job.status === "completed" && (
                                    <span className="text-emerald-400/70">
                                      {job.total_panels} panel{job.total_panels !== 1 ? "s" : ""}
                                    </span>
                                  )}
                                  {job.detector === "full-page" && (
                                    <span className="text-amber-500/80"> · no model</span>
                                  )}
                                  {job.detector === "yolo+fallback" && (
                                    <span className="text-amber-500/80"> · partial detect</span>
                                  )}
                                  {job.extracted_to && (
                                    <span className="text-blue-400/70"> · 📁 extracted</span>
                                  )}
                                  {job.created_at && (
                                    <span className="text-slate-600">
                                      {" · "}
                                      {new Date(job.created_at).toLocaleString()}
                                    </span>
                                  )}
                                </p>
                                </span>
                              </button>
                              <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                {job.source_url && (
                                  <a
                                    href={job.source_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="View original PDF"
                                    className="text-slate-400 hover:text-slate-200 text-xs"
                                  >
                                    📄
                                  </a>
                                )}
                                <button
                                  onClick={() => handleExtract(job)}
                                  disabled={busy === job.job_id || job.total_panels === 0}
                                  title={`Create folder "${job.filename.replace(/\.pdf$/i, "")}" with these charts`}
                                  className="text-blue-400 hover:text-blue-300 disabled:text-slate-700"
                                >
                                  {busy === job.job_id ? "…" : "📁+"}
                                </button>
                                <button
                                  onClick={() => handleDeleteJob(job)}
                                  title="Delete this PDF and its panels"
                                  className="text-slate-600 hover:text-red-400"
                                >
                                  🗑
                                </button>
                              </div>
                            </div>

                            {open && (
                              <div className="border-t border-rdp-border p-2.5">
                                {job.panels.length === 0 ? (
                                  <p className="text-[11px] text-slate-600">No panels left.</p>
                                ) : (
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {job.panels.map((p) => (
                                      <div
                                        key={p.name}
                                        className={`group relative rounded overflow-hidden border-2 ${
                                          p.corrected
                                            ? "border-emerald-500/70"
                                            : p.has_trace || p.has_ocr
                                              ? "border-cyan-700/60"
                                              : "border-rdp-border"
                                        }`}
                                      >
                                        <button
                                          onClick={() => setReview({ job, panel: p })}
                                          title="Review: compare with source page"
                                          className="block w-full"
                                        >
                                          <img
                                            src={p.url}
                                            alt={p.name}
                                            loading="lazy"
                                            className="w-full h-24 object-contain bg-black/30 hover:opacity-80 transition-opacity cursor-pointer"
                                          />
                                        </button>
                                        {/* Saved / progress badges — visible at a glance so an operator
                                            can tell which panels still need attention. */}
                                        <div className="absolute top-1 left-1 flex gap-1 pointer-events-none">
                                          {p.corrected && (
                                            <span
                                              className="bg-emerald-500/95 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded shadow"
                                              title="Axis positions or crop bbox manually confirmed and saved"
                                            >
                                              ✓ Saved
                                            </span>
                                          )}
                                          {!p.corrected && p.has_ocr && (
                                            <span
                                              className="bg-cyan-600/90 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded shadow"
                                              title="OCR ran but no manual confirmation yet"
                                            >
                                              OCR
                                            </span>
                                          )}
                                          {p.has_trace && (
                                            <span
                                              className="bg-orange-500/90 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded shadow"
                                              title="Point finding produced a result"
                                            >
                                              Traced
                                            </span>
                                          )}
                                        </div>
                                        <button
                                          onClick={() => handleDeletePanel(job, p.name)}
                                          title="Delete this panel"
                                          className="absolute top-1 right-1 bg-black/70 rounded px-1.5 py-0.5 text-[10px] text-slate-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                          🗑
                                        </button>
                                        <div className="px-1.5 py-1">
                                          <p className="text-[10px] text-slate-400 truncate">
                                            {p.name}
                                          </p>
                                          <p className="text-[9px] text-slate-600">
                                            {(p.size / 1024).toFixed(0)} KB
                                          </p>
                                        </div>
                                        <CropActions
                                          path={path}
                                          filename={p.name}
                                          jobId={job.job_id}
                                        />
                                        <PanelExports
                                          path={path}
                                          jobId={job.job_id}
                                          filename={p.name}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>

    {review && (
      <CropReviewModal
        path={path}
        jobId={review.job.job_id}
        panel={review.panel}
        onClose={() => setReview(null)}
        onReplaced={() => {
          // Don't close the modal — the operator is mid-review and wants to
          // see the new crop reflected immediately.  The child forces a
          // cache-bust reload of the image; we also kick off a folder refresh
          // in the background so the tile in the grid updates too.
          void refresh(path);
        }}
      />
    )}
    </>
  );
}
